import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import backend from "../../backend";

/**
 * PromoverGuiaModal — convierte un delivery tipo "guia" a "factura"/"boleta".
 *
 * Props:
 *   target  { workOrderId, deliveryId, qty, guiaNumber, guiaDate, itemLabel } | null
 *   onClose, onSaved
 *
 * Backend: PATCH /workOrder/:id/delivery/:deliveryId con { docType, docNumber, docDate }.
 * Mueve qty de qtyDelivered → qtyInvoiced. Preserva el N° de guía original
 * en delivery.linkedGuia para audit.
 */
const PromoverGuiaModal = ({ target, onClose, onSaved }) => {
  const [docType, setDocType]     = useState("factura");
  const [docNumber, setDocNumber] = useState("");
  const [docDate, setDocDate]     = useState("");
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState("");

  useEffect(() => {
    if (!target) return;
    setDocType("factura");
    setDocNumber("");
    setDocDate(new Date().toISOString().slice(0, 10));
    setError("");
  }, [target]);

  if (!target) return null;

  const submit = async () => {
    setError("");
    if (!docNumber.trim()) { setError("N° de factura requerido"); return; }
    setSaving(true);
    try {
      const res = await backend.patch(
        `workOrder/${target.workOrderId}/delivery/${target.deliveryId}`,
        {
          docType,
          docNumber: docNumber.trim(),
          docDate: docDate ? new Date(docDate).toISOString() : new Date().toISOString(),
        }
      );
      onSaved?.(res.data);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Error al promover");
    }
    setSaving(false);
  };

  return ReactDOM.createPortal(
    <div
      onMouseDown={e => { if (e.target === e.currentTarget && !saving) onClose?.(); }}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 10001,
      }}
    >
      <div style={{
        background: "#1a1a1a", border: "1px solid #333", borderRadius: 8,
        width: "min(480px, 92vw)", padding: "18px 22px", color: "#ddd",
        fontSize: 12, fontFamily: "Arial, sans-serif",
      }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#fff" }}>
            Facturar guía existente
          </div>
          <button
            onClick={() => !saving && onClose?.()}
            style={{ background:"transparent", border:"none", color: "#cbd5e1", fontSize:18, cursor:"pointer", lineHeight:1 }}
          >×</button>
        </div>

        <div style={{
          background:"#161616", border:"1px solid #2e2e2e", borderRadius: 4,
          padding: "10px 12px", marginBottom: 14, fontSize: 11, lineHeight: 1.5,
        }}>
          <div style={{ color: "#cbd5e1", fontSize: 10, textTransform:"uppercase", letterSpacing: 0.5 }}>
            Guía a facturar
          </div>
          <div style={{ color:"#fff", marginTop: 4 }}>
            📄 Guía <strong style={{ color:"#3498db" }}>{target.guiaNumber}</strong> · {target.qty} unidad{target.qty === 1 ? "" : "es"}
            {target.guiaDate && (
              <span style={{ color: "#aaa", marginLeft: 8 }}>
                emitida {new Date(target.guiaDate).toLocaleDateString("es-CL")}
              </span>
            )}
          </div>
          {target.itemLabel && (
            <div style={{ color:"#aaa", marginTop: 4, fontSize: 11 }}>
              {target.itemLabel}
            </div>
          )}
          <div style={{ color: "#aaa", marginTop: 8, fontSize: 10 }}>
            Al facturar, las {target.qty} unidad{target.qty === 1 ? "" : "es"} pasarán a contar como facturadas en el mes de la factura. El N° de guía queda registrado como audit.
          </div>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap: 10 }}>
          <Field label="Tipo de documento">
            <select
              value={docType} onChange={e => setDocType(e.target.value)}
              className="bini-input" style={{ height: 32 }}
            >
              <option value="factura">Factura</option>
              <option value="boleta">Boleta</option>
            </select>
          </Field>
          <Field label="Fecha del DTE">
            <input
              type="date" className="bini-input" value={docDate}
              onChange={e => setDocDate(e.target.value)}
            />
          </Field>
          <div style={{ gridColumn: "1 / -1" }}>
            <Field label={`N° ${docType} *`}>
              <input
                className="bini-input bini-mono" value={docNumber}
                onChange={e => setDocNumber(e.target.value)}
                placeholder="ej: 4567"
                autoFocus
              />
            </Field>
          </div>
        </div>

        {error && (
          <div style={{
            color:"#e74c3c", fontSize: 11, marginTop: 10,
            background:"rgba(231,76,60,0.08)", padding:"6px 10px", borderRadius: 4,
            border:"1px solid rgba(231,76,60,0.3)",
          }}>{error}</div>
        )}

        <div style={{ display:"flex", justifyContent:"flex-end", gap: 8, marginTop: 14 }}>
          <button
            onClick={() => !saving && onClose?.()}
            disabled={saving}
            style={{
              background:"transparent", border:"1px solid #444",
              color:"#aaa", borderRadius: 5, padding:"7px 14px",
              fontSize: 12, cursor:"pointer",
            }}
          >Cancelar</button>
          <button
            onClick={submit}
            disabled={saving}
            style={{
              background:"#2ecc71", border:"none", color:"#0d2a15",
              borderRadius: 5, padding:"7px 16px",
              fontSize: 12, fontWeight: 700,
              cursor: saving ? "wait" : "pointer",
              opacity: saving ? 0.7 : 1,
            }}
          >{saving ? "Guardando…" : `Registrar ${docType}`}</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const Field = ({ label, children }) => (
  <div>
    <div style={{
      fontSize: 9.5, fontWeight: 700, color: "#cbd5e1",
      textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 3,
    }}>{label}</div>
    {children}
  </div>
);

export default PromoverGuiaModal;
